import { IMG, WILLIAM_QUOTE, CONTACT_INFO } from "../data/content";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        title="About — William Martin & the SeedKeepers mission"
        description="Founded by William Martin (supporting seed banks since 2002), SeedKeepers helps Indigenous communities and organisations build community seed banks and restoration infrastructure across Australia."
        path="/about"
      />
      {/* Purpose */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="section-num mb-4">About — Our Purpose</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              Biodiversity protection, nature restoration and seed preservation — with <em className="text-eucalypt">community seed banks.</em>
            </h1>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              We are living in a once-in-a-civilization moment, with profound
              consequences for the fabric of life on earth. Rebuilding food
              security by preserving, protecting and proliferating native seed
              systems is of vital importance.
            </p>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              SeedKeepers was established to support remote communities in
              establishing their own native seed banks and biodiversity
              registration — and to build the biological infrastructure behind
              expert-led ecological restoration across Australia.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img src={IMG.siftingHands} alt="Hands sifting seed in a bowl" className="w-full h-[420px] object-cover" />
            </div>
            <p className="mt-3 text-xs text-muted italic">
              Country in the hand. Seed is life.
            </p>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="specimen-label text-chalk/60 mb-6">The Power of the Seed</p>
          <blockquote className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
            &ldquo;{WILLIAM_QUOTE}&rdquo;
          </blockquote>
          <p className="mt-8 specimen-label text-ochre">— William Martin · Founder · Supporting seed banks since {CONTACT_INFO.founderSince}</p>
        </div>
      </section>

      {/* Founder */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <img src={IMG.william} alt="William Martin, founder of SeedKeepers" className="w-full h-[460px] object-cover border border-rule p-2 bg-cream" />
            <p className="mt-3 text-xs text-muted italic">William Martin — Founder.</p>
          </div>
          <div className="lg:col-span-7">
            <p className="section-num mb-4">Decades of Experience</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              Our founder, William Martin, has been supporting seed banks and seed production since 2002.
            </h2>
            <p className="mt-8 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
              SeedKeepers was founded to address the core challenges of our
              times — and offers an empowering horticultural movement, one
              that views management of Traditional Cultural Knowledge as
              social justice.
            </p>
            <p className="mt-4 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
              Native plant species around the world are disappearing every day
              due to habitat loss, climate change, severe wildfire seasons and
              extreme weather events. As these plant populations are lost, so
              too are the ecosystem services they provide — and the loss
              threatens the Traditional communities which rely on these
              varieties for food, medicines and cultural use.
            </p>
            <p className="mt-4 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
              Community seed banks have emerged as a vital community resource —
              not only for food and ecological security, but for the economic,
              political and social empowerment of communities.
            </p>
          </div>
        </div>
      </section>

      {/* Founder's Story (first person) */}
      <section className="border-b border-rule bg-parchment/40" data-testid="about-founder-story">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="section-num mb-4">From the Founder</p>
              <p className="specimen-label mb-6">A note from William Martin</p>
              <blockquote className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.08] text-ink">
                For more than three decades, my work has explored a single question:
              </blockquote>
              <p className="mt-6 font-serif italic text-2xl md:text-3xl text-eucalypt leading-snug">
                How do we preserve and carry forward the forces that sustain life?
              </p>

              <div className="mt-12 border-l-2 border-ochre2 pl-6">
                <p className="specimen-label text-ochre2 mb-3">Working Principle</p>
                <p className="font-serif text-xl md:text-2xl leading-snug text-ink/90">
                  I work with a small number of aligned organisations where there is a
                  genuine commitment to long-term outcomes for Country, culture and
                  community.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="space-y-6 text-base md:text-lg text-ink/85 leading-relaxed">
                <p>
                  That question has taken me from recording the stories of Apache
                  Elders in the forests of Arizona, to working alongside Tony
                  Robbins, Anita Roddick and Dr Vandana Shiva, to living and working
                  with Indigenous communities in remote Australia.
                </p>
                <p>
                  Over the past decade, my professional focus has centred on
                  Indigenous governance, conservation, land management and
                  restoration. I have worked alongside Traditional Owners,
                  government and industry to develop Healthy Country Planning
                  initiatives, protected area partnerships, cultural heritage
                  programs and expert-led approaches to stewardship and
                  on-Country decision-making.
                </p>
                <p>
                  Today, my work sits at the intersection of Indigenous knowledge,
                  ecological restoration and economic development.
                </p>
                <p>
                  Through SeedKeepers and Bush Botanics, I am focused on building
                  the systems needed to support Australia&rsquo;s emerging restoration
                  economy — connecting native seed, biodiversity, cultural
                  knowledge and long-term livelihoods into practical, scalable
                  models that create lasting benefit for Country and community.
                </p>
              </div>

              {/* Current focus */}
              <div className="mt-12">
                <p className="specimen-label mb-5">Current focus</p>
                <ul
                  className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule"
                  data-testid="founder-current-focus"
                >
                  {[
                    "Regional native seed readiness and restoration pathways",
                    "expert-led seed, bush food and restoration enterprises",
                    "Strategic partnerships between Traditional Owners, government and industry",
                    "Restoration investment readiness and project development",
                    "Ranger workforce capability and environmental market participation",
                    "Native seed systems, provenance and long-term supply chain resilience",
                  ].map((f, i) => (
                    <li
                      key={f}
                      className="bg-card p-5 md:p-6 flex gap-4 items-start"
                      data-testid={`founder-focus-${i}`}
                    >
                      <span className="font-mono text-[10px] tracking-[0.22em] text-ochre2 mt-1 flex-shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="font-serif text-lg md:text-xl leading-snug">{f}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Belief */}
              <p className="mt-12 font-serif italic text-2xl md:text-3xl text-eucalypt leading-snug max-w-3xl">
                I believe the future of conservation in Australia will be
                Indigenous-led, restoration-focused and grounded in stewardship.
              </p>
            </div>
          </div>

          {/* Origin recognition — signature card */}
          <div className="mt-16 md:mt-20 border border-rule bg-bark text-chalk p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-9">
                <p className="specimen-label text-ochre mb-5">Origin</p>
                <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.15] text-chalk">
                  SeedKeepers emerged from a simple recognition: that the future
                  depends on our ability to{" "}
                  <em className="text-ochre">steward and transmit the living systems</em>{" "}
                  that sustain life, culture and Country.
                </p>
              </div>
              <div className="md:col-span-3 md:border-l md:border-chalk/15 md:pl-8">
                <p className="specimen-label text-chalk/60 mb-3">Contact</p>
                <p className="font-serif text-lg md:text-xl text-chalk mb-1">William</p>
                <Link
                  to="/contact"
                  data-testid="founder-contact-cta"
                  className="link-mono text-ochre inline-flex items-center gap-2"
                >
                  seedkeepers.com.au →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="border-b border-rule">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">Our Approach</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.04] max-w-3xl">
            Listening to community. Guided by cultural authority. <em className="text-eucalypt">Building restoration systems for the long term.</em>
          </h2>
          <p className="mt-10 font-serif italic text-2xl md:text-3xl text-muted leading-snug max-w-3xl">
            The most important work often happens before any seed is collected.
          </p>
        </div>
      </section>

      {/* Listen / Partner */}
      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="border border-rule bg-card p-8 md:p-10">
            <p className="specimen-label mb-4">In the Media</p>
            <h3 className="font-serif text-2xl md:text-3xl mb-3">The Seed Savers — ABC Earshot</h3>
            <p className="text-sm text-muted leading-relaxed mb-6">
              Listen to the ABC Earshot podcast on the seed savers — the story
              of community seed banks and the people who steward them.
            </p>
            <a href={CONTACT_INFO.podcast.url} target="_blank" rel="noopener noreferrer" className="link-mono text-eucalypt" data-testid="about-podcast-link">
              Listen on ABC →
            </a>
          </div>
          <div className="border border-rule bg-card p-8 md:p-10">
            <p className="specimen-label mb-4">Partner Project</p>
            <h3 className="font-serif text-2xl md:text-3xl mb-3">National Indigenous Seed Bank Project</h3>
            <p className="text-sm text-muted leading-relaxed mb-6">
              SeedKeepers contributes to and works alongside the National
              Indigenous Seed Bank Project to grow Australia&rsquo;s community seed
              bank network.
            </p>
            <a href={CONTACT_INFO.partner.url} target="_blank" rel="noopener noreferrer" className="link-mono text-eucalypt" data-testid="about-partner-link">
              Visit nisb.com.au →
            </a>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-10 pb-24 text-center">
          <p className="font-serif italic text-4xl sm:text-5xl md:text-6xl text-eucalypt my-6">SEED IS LIFE.</p>
          <Link to="/contact" className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all" data-testid="about-contact-cta">
            Seed What Happens →
          </Link>
        </div>
      </section>
    </div>
  );
}
